/**
 * TranscriptView: a aba "Transcrição" do painel da chamada (esboço 3.3), só visual: busca, filtros (Tudo e
 * Momentos), uma entrada por pessoa com avatar, nome e hora, os momentos marcados no ponto onde aconteceram,
 * pontinhos de quem está falando e o rodapé com "Exportar". Os dados e as ações vêm de `TranscriptPanel`.
 *
 * Os filtros "Perguntas" e "Ações" do esboço dependem da análise da Jev (fase seguinte) e não existem ainda.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, Download, Loader2, Search, TriangleAlert, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { initialsOf, toneFor } from '../room/participantDisplay';
import { filterSegments, groupTurns, segmentAt, splitByMatches, timeLabel } from './transcriptProtocol';

const YOU = 'Você';

/** Quão perto do fim conta como "acompanhando": dentro disso, o texto novo rola a lista sozinho. */
const STICK_PX = 48;

function Avatar({ name, identity }) {
    return (
        <span
            aria-hidden="true"
            className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white', toneFor(identity ?? name))}
        >
            {initialsOf(name)}
        </span>
    );
}

function Highlighted({ text, query }) {
    return splitByMatches(text, query).map((part, index) => (
        part.match
            ? <mark key={index} className="rounded-sm bg-amber-300/35 px-0.5 text-inherit">{part.text}</mark>
            : <span key={index}>{part.text}</span>
    ));
}

/** Os três pontinhos de quem está falando (ou acabou de falar e o texto ainda não chegou). */
function SpeakingEntry({ person, you }) {
    const name = you ? YOU : (person.name || 'Participante');
    return (
        <div data-speaking-entry={person.identity} className="flex gap-3 px-4 py-2">
            <Avatar name={person.name || name} identity={person.identity} />
            <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-50">{name}</p>
                <p role="img" aria-label={`${name} está falando`} className="mt-1 flex gap-1" data-typing="">
                    {[0, 1, 2].map((i) => (
                        <span
                            key={i}
                            className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-300 motion-reduce:animate-none"
                            style={{ animationDelay: `${i * 160}ms` }}
                        />
                    ))}
                </p>
            </div>
        </div>
    );
}

function TurnEntry({ turn, origin, query, you, flash }) {
    return (
        <div data-turn={turn.key} data-at={turn.startedAt} className={cn('flex gap-3 px-4 py-2 transition-colors duration-700', flash && 'bg-cyan-400/10')}>
            <Avatar name={turn.name} identity={turn.identity} />
            <div className="min-w-0 flex-1">
                <p className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-semibold text-neutral-50">{you ? YOU : turn.name}</span>
                    <time className="font-mono text-[11px] tabular-nums text-neutral-400">{timeLabel(turn.startedAt, origin)}</time>
                </p>
                <p className="mt-0.5 break-words text-sm leading-relaxed text-neutral-100">
                    <Highlighted text={turn.text} query={query} />
                </p>
            </div>
        </div>
    );
}

function MomentCard({ moment, origin, context, onJump, onRemove }) {
    const label = timeLabel(moment.at, origin);
    return (
        <div data-moment={moment.id} className="mx-4 my-1.5 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2">
            <div className="flex items-center gap-2 text-sm">
                <Bookmark className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
                <span className="min-w-0 font-semibold text-neutral-50">Momento marcado</span>
                <span className="font-mono text-[11px] tabular-nums text-neutral-300">{label}</span>
                <span className="ml-auto flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onJump(moment.at)}
                        className="whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-semibold text-cyan-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
                    >
                        {`Ir para ${label}`}
                    </button>
                    <button
                        type="button"
                        aria-label={`Remover o momento de ${label}`}
                        onClick={() => onRemove(moment.id)}
                        className="rounded-md p-1 text-neutral-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
                    >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </span>
            </div>
            {context && <p className="mt-1 line-clamp-2 text-xs text-neutral-300">{context.text}</p>}
        </div>
    );
}

function Empty({ children }) {
    return <p className="px-6 py-10 text-center text-sm text-neutral-300">{children}</p>;
}

/** Por que a lista está vazia: o painel nunca fica mudo sem explicar. */
const EMPTY_BY_STATE = Object.freeze({
    present: 'A transcrição aparece aqui à medida que vocês falam.',
    waiting: 'A transcrição começa quando a chamada começar.',
    starting: 'Iniciando a transcrição…',
    unavailable: 'Nenhuma fala transcrita até agora.',
});

/** O motivo, em palavras, quando a Jev não entra na sala (códigos de `TranscriberDiagnosis`, no domínio). */
const DIAGNOSIS_TEXT = Object.freeze({
    worker_down: 'O serviço de transcrição não está em execução neste ambiente.',
    standby: 'O serviço de transcrição está ativo só na versão em espera do deploy.',
    waiting: 'O serviço de transcrição está no ar, mas ainda não assumiu esta chamada.',
    in_room: 'O serviço diz estar na sala, mas ele não aparece para você (identidade ou permissão da sala).',
});
const JOIN_FAILED_TEXT = Object.freeze({
    media_unreachable: 'O transcritor não conseguiu conectar ao servidor de vídeo.',
    tls_certificates: 'O transcritor não consegue validar o certificado do servidor de vídeo (a imagem do serviço de transcrição precisa dos certificados raiz instalados).',
    signal_unreachable: 'O transcritor não consegue abrir a conexão com o endereço do servidor de vídeo (DNS, rede, certificado ou proxy).',
    media_blocked: 'O transcritor chega ao servidor de vídeo, mas a mídia não conecta (as portas UDP e TCP do servidor de vídeo estão liberadas para este servidor?).',
    auth: 'O servidor de vídeo não aceitou as chaves do transcritor.',
    config: 'Falta configuração do servidor de vídeo no serviço de transcrição.',
    native_module: 'O módulo de áudio do serviço de transcrição não carregou neste servidor.',
    other: 'O transcritor falhou ao entrar na sala.',
});

/** @param {{ state: string, code?: string|null } | null | undefined} diagnosis @returns {string|null} */
export const diagnosisText = (diagnosis) => {
    if (!diagnosis) return null;
    if (diagnosis.state === 'join_failed') return JOIN_FAILED_TEXT[diagnosis.code] ?? JOIN_FAILED_TEXT.other;
    return DIAGNOSIS_TEXT[diagnosis.state] ?? null;
};

const chip = 'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300';
const chipOn = 'border-cyan-400/60 bg-cyan-400/15 text-cyan-200';
const chipOff = 'border-white/15 text-neutral-200 hover:bg-white/10';

/**
 * @param {{
 *   status: 'loading'|'ready'|'error', segments: import('./transcriptProtocol').Segment[],
 *   moments: Array<{ id: string, at: number }>, origin: number|null,
 *   indicators: Array<{ identity: string, name: string }>, localIdentity: string|null,
 *   transcriberState?: 'present'|'waiting'|'starting'|'unavailable', diagnosis?: { state: string, code?: string|null } | null,
 *   compact?: boolean,
 *   query: string, onQueryChange: (q: string) => void, filter: 'all'|'moments', onFilterChange: (f: 'all'|'moments') => void,
 *   onRemoveMoment: (id: string) => void, onExport: () => void, onRetry: () => void,
 * }} props
 */
export function TranscriptView({
    status, segments, moments, origin, indicators, localIdentity, transcriberState = 'present', diagnosis = null, compact = false, query,
    onQueryChange, filter, onFilterChange, onRemoveMoment, onExport, onRetry,
}) {
    const searching = query.trim().length > 0;
    const turns = useMemo(() => groupTurns(filterSegments(segments, query)), [segments, query]);

    // Na lista de "Tudo" os momentos entram no ponto onde aconteceram; com busca, só o texto.
    const items = useMemo(() => {
        if (filter !== 'all') return [];
        const entries = turns.map((turn) => ({ kind: 'turn', at: turn.startedAt, turn }));
        if (!searching) entries.push(...moments.map((moment) => ({ kind: 'moment', at: moment.at, moment })));
        return entries.sort((a, b) => a.at - b.at || (a.kind === 'turn' ? -1 : 1));
    }, [filter, turns, moments, searching]);

    const scroller = useRef(null);
    const stick = useRef(true);
    const [flash, setFlash] = useState(null);

    const visibleIndicators = filter === 'all' && !searching ? indicators : [];
    const contentSize = items.length + visibleIndicators.length;

    // Acompanha o fim da conversa, a não ser que a pessoa tenha rolado para ler algo mais antigo.
    useEffect(() => {
        const el = scroller.current;
        if (el && stick.current) el.scrollTop = el.scrollHeight;
    }, [contentSize, status]);

    // "Ir para 12:10": volta para "Tudo" (sem busca) e, com a lista na tela, rola até a fala daquela hora e a
    // destaca por um instante. O pedido fica pendente até a lista de "Tudo" existir.
    const [pendingJump, setPendingJump] = useState(null);
    const requestJump = (at) => {
        onQueryChange('');
        onFilterChange('all');
        setPendingJump(at);
    };
    useEffect(() => {
        if (pendingJump == null || filter !== 'all' || searching) return;
        const target = [...turns].reverse().find((t) => t.startedAt <= pendingJump) ?? turns[0];
        setPendingJump(null);
        if (!target) return;
        scroller.current?.querySelector(`[data-turn="${CSS.escape(target.key)}"]`)?.scrollIntoView?.({ block: 'center' });
        stick.current = false;
        setFlash(target.key);
    }, [pendingJump, filter, searching, turns]);

    // O destaque some sozinho. Timer à parte: o efeito acima roda de novo ao zerar `pendingJump` e o cancelaria.
    useEffect(() => {
        if (flash == null) return undefined;
        const timer = setTimeout(() => setFlash(null), 1600);
        return () => clearTimeout(timer);
    }, [flash]);

    const onScroll = () => {
        const el = scroller.current;
        if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
    };

    const momentCards = filter === 'moments' ? moments : [];

    let body;
    if (status === 'loading') {
        body = <p className="flex items-center justify-center gap-2 px-6 py-10 text-sm text-neutral-300"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Carregando a transcrição…</p>;
    } else if (status === 'error' && segments.length === 0) {
        body = (
            <div role="alert" className="px-6 py-10 text-center text-sm text-neutral-200">
                <p>Não foi possível carregar a transcrição.</p>
                <button type="button" onClick={onRetry} className="mt-3 rounded-md border border-white/20 px-3 py-1.5 text-xs font-medium hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300">
                    Tentar de novo
                </button>
            </div>
        );
    } else if (filter === 'moments') {
        body = momentCards.length === 0
            ? <Empty>Nenhum momento marcado ainda. Use o marcador na barra da chamada.</Empty>
            : momentCards.map((moment) => (
                <MomentCard
                    key={moment.id}
                    moment={moment}
                    origin={origin}
                    context={segmentAt(segments, moment.at)}
                    onJump={requestJump}
                    onRemove={onRemoveMoment}
                />
            ));
    } else if (items.length === 0 && visibleIndicators.length === 0) {
        body = <Empty>{searching ? `Nada encontrado para “${query.trim()}”.` : EMPTY_BY_STATE[transcriberState] ?? EMPTY_BY_STATE.present}</Empty>;
    } else {
        body = (
            <>
                {items.map((item) => (item.kind === 'turn'
                    ? <TurnEntry key={item.turn.key} turn={item.turn} origin={origin} query={query} you={item.turn.identity === localIdentity} flash={flash === item.turn.key} />
                    : <MomentCard key={item.moment.id} moment={item.moment} origin={origin} context={null} onJump={requestJump} onRemove={onRemoveMoment} />))}
                {visibleIndicators.map((person) => <SpeakingEntry key={person.identity} person={person} you={person.identity === localIdentity} />)}
            </>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className={cn(compact ? 'flex items-center gap-2 px-3 pb-1.5 pt-2' : 'space-y-2.5 px-4 pb-2 pt-3')}>
                <label className={cn('relative block', compact && 'min-w-0 flex-1')}>
                    <span className="sr-only">Buscar na transcrição</span>
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" aria-hidden="true" />
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        placeholder={compact ? 'Buscar' : 'Buscar na transcrição'}
                        className="h-9 w-full rounded-lg border border-white/15 bg-white/5 pl-9 pr-3 text-sm text-neutral-50 placeholder:text-neutral-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
                    />
                </label>
                <div role="group" aria-label="Filtrar a transcrição" className={cn('flex gap-2', compact ? 'shrink-0' : 'flex-wrap')}>
                    <button type="button" aria-pressed={filter === 'all'} onClick={() => onFilterChange('all')} className={cn(chip, filter === 'all' ? chipOn : chipOff)}>
                        Tudo
                    </button>
                    <button type="button" aria-pressed={filter === 'moments'} onClick={() => onFilterChange('moments')} className={cn(chip, filter === 'moments' ? chipOn : chipOff)}>
                        Momentos
                        <span data-count="moments" className="rounded-full bg-white/15 px-1.5 text-[10px] tabular-nums">{moments.length}</span>
                    </button>
                </div>
            </div>

            {transcriberState === 'unavailable' && (
                <p
                    role="status"
                    data-transcriber-state="unavailable"
                    className="mx-4 mb-2 flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs leading-snug text-amber-100"
                >
                    <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>
                        A transcrição está indisponível agora. A chamada continua normalmente, mas o que for dito não será transcrito.
                        {diagnosisText(diagnosis) && (
                            <span data-diagnosis={diagnosis.state} className="mt-1 block text-amber-200/90">
                                {`Motivo: ${diagnosisText(diagnosis)}`}
                            </span>
                        )}
                    </span>
                </p>
            )}

            <div
                ref={scroller}
                onScroll={onScroll}
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                aria-label="Transcrição da chamada"
                className="min-h-0 flex-1 overflow-y-auto py-1"
            >
                {body}
            </div>

            <footer className={cn('flex items-center justify-between gap-2 border-t border-white/10 text-xs text-neutral-300', compact ? 'px-3 py-1' : 'px-4 py-2.5')}>
                <span className="truncate">Português (Brasil)</span>
                <button
                    type="button"
                    onClick={onExport}
                    disabled={segments.length === 0}
                    className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-semibold text-neutral-50 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 disabled:opacity-50 disabled:hover:bg-transparent"
                >
                    <Download className="h-3.5 w-3.5" aria-hidden="true" />
                    Exportar
                </button>
            </footer>
        </div>
    );
}
